/* eslint-disable prefer-arrow-callback */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { CircularProgress } from '@mui/material';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import { LoadingWrapper } from '@linetweet/linetweet-ui';
import {
  CalendarOverviewGridProps,
  CalendarGridProps,
  ICalendarWeeksState,
  IWeek,
  LoaderElementKeysEnum,
  WeekRowContentProps,
  WeekDaysRowProps,
  LoaderRowProps,
} from '../types';
import { buildInitialWeeks, getWeeksToPrepend, getWeeksToAppend } from '../lib';
import { TimeHelper } from '../../../utils';

import styles from './CalendarOverviewGrid.module.scss';

const ROW_HEIGHT = 211;
const WEEK_DAYS_ROW_HEIGHT = 74;
const WEEKS_PER_PAGE = 5;
const EXTRA_START_ROWS_COUNT = 1;
const EXTRA_END_ROWS_COUNT = 1;

const WeekRowContent = React.memo(function WeekRowContentComponent({ week, DayContentSlot }: WeekRowContentProps) {
  return (
    <div className={styles.week_row} style={{ height: `${ROW_HEIGHT}px` }}>
      {week.dateKeys.map((dateKey, index) => (
        <div
          key={dateKey}
          data-key={dateKey}
          className={styles.day_container}
        >
          <DayContentSlot dateKey={dateKey} date={week.unixDates[index]} />
        </div>
      ))}
    </div>
  );
});

const WeekDaysRow = React.memo(function WeekDaysRowComponent({ timezone }: WeekDaysRowProps) {
  const weekDays = useMemo(() => TimeHelper.getWeekDaysShort(timezone), []);

  return (
    <div className={clsx(styles.week_row, styles.week_days_row)} style={{ height: `${WEEK_DAYS_ROW_HEIGHT}px` }}>
      {weekDays.map((weekDay) => (
        <div key={weekDay} className={styles.week_day}>{weekDay}</div>
      ))}
    </div>
  );
});

const LoaderRow = React.memo(function LoaderRowComponent({ offsetTop }: LoaderRowProps) {
  return (
    <div
      className={styles.loader_row}
      style={{
        height: `${ROW_HEIGHT}px`,
        ...(offsetTop && {
          height: `${ROW_HEIGHT + offsetTop}px`,
          paddingTop: `${offsetTop}px`,
        }),
      }}
    >
      <CircularProgress disableShrink />
    </div>
  );
});

const CalendarGrid = React.memo(function CalendarGridComponent(props: CalendarGridProps) {
  const { containerRef, virtualizer, virtualItems, calendarWeeks, DayContentSlot, timezone } = props;

  const renderRowContent = useCallback((virtualItem: VirtualItem) => {
    if (virtualItem.key === LoaderElementKeysEnum.PREV_LOADER || virtualItem.key === LoaderElementKeysEnum.NEXT_LOADER) {
      let offsetTop = 0;
      if (virtualItem.key === LoaderElementKeysEnum.PREV_LOADER) {
        offsetTop = WEEK_DAYS_ROW_HEIGHT;
      }

      return <LoaderRow offsetTop={offsetTop} />;
    }

    return (
      <WeekRowContent
        week={calendarWeeks.weeks[virtualItem.index - EXTRA_START_ROWS_COUNT]}
        DayContentSlot={DayContentSlot}
      />
    );
  }, [calendarWeeks.weeks]);

  return (
    <div className={styles.calendar_container} ref={containerRef}>
      <div className={styles.calendar_grid} style={{ height: `${virtualizer.getTotalSize()}px` }}>
        <WeekDaysRow timezone={timezone} />
        {virtualItems.map((virtualItem) => (
          <div
            key={String(virtualItem.key)}
            data-index={virtualItem.index}
            data-key={virtualItem.key}
            ref={virtualizer.measureElement}
            className={styles.week_row_container}
            style={{
              transform: `translateY(${virtualItem.start}px) translateZ(0)`,
            }}
          >
            {renderRowContent(virtualItem)}
          </div>
        ))}
      </div>
    </div>
  );
});

export const CalendarOverviewGrid = React.memo(function CalendarOverviewGridComponent(props: CalendarOverviewGridProps) {
  const { initialDate, timezone, onLoadData, DayContentSlot } = props;
  const [calendarWeeks, setCalendarWeeks] = useState<ICalendarWeeksState>(() => buildInitialWeeks(initialDate, timezone));
  const containerRef = useRef<HTMLDivElement>(null);
  const isFetchingPrevMonthRef = useRef<boolean>(false);
  const isFetchingNextMonthRef = useRef<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const initialize = useCallback(async (initialWeeks: IWeek[]) => {
    await onLoadData(initialWeeks);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) return;

    initialize(calendarWeeks.weeks);
  }, [isInitialized, calendarWeeks]);

  const handleLoadPrevMonth = useCallback(async (currentCalendarWeeks: IWeek[], storeTimezone: string) => {
    isFetchingPrevMonthRef.current = true;

    const weeksToPrepend = getWeeksToPrepend(currentCalendarWeeks, WEEKS_PER_PAGE, storeTimezone);

    await onLoadData(weeksToPrepend);

    setCalendarWeeks((calendarWeeksState) => {
      const filteredWeeksToPrepend: IWeek[] = [];
      const updatedAddedWeekKeysMap = { ...calendarWeeksState.addedWeekKeysMap };

      for (let weekIndex = 0; weekIndex < weeksToPrepend.length; weekIndex += 1) {
        const { weekKey } = weeksToPrepend[weekIndex];
        if (!updatedAddedWeekKeysMap[weekKey]) {
          updatedAddedWeekKeysMap[weekKey] = true;
          filteredWeeksToPrepend.push(weeksToPrepend[weekIndex]);
        }
      }

      return {
        weeks: [...filteredWeeksToPrepend, ...calendarWeeksState.weeks],
        addedWeekKeysMap: updatedAddedWeekKeysMap,
      };
    });

    isFetchingPrevMonthRef.current = false;
  }, []);

  const handleLoadNextMonth = useCallback(async (currentCalendarWeeks: IWeek[], storeTimezone: string) => {
    isFetchingNextMonthRef.current = true;

    const weeksToAppend = getWeeksToAppend(currentCalendarWeeks, WEEKS_PER_PAGE, storeTimezone);

    await onLoadData(weeksToAppend);

    setCalendarWeeks((calendarWeeksState) => {
      const filteredWeeksToAppend: IWeek[] = [];
      const updatedAddedWeekKeysMap = { ...calendarWeeksState.addedWeekKeysMap };

      for (let weekIndex = 0; weekIndex < weeksToAppend.length; weekIndex += 1) {
        const { weekKey } = weeksToAppend[weekIndex];
        if (!updatedAddedWeekKeysMap[weekKey]) {
          updatedAddedWeekKeysMap[weekKey] = true;
          filteredWeeksToAppend.push(weeksToAppend[weekIndex]);
        }
      }

      return {
        weeks: [...calendarWeeksState.weeks, ...filteredWeeksToAppend],
        addedWeekKeysMap: updatedAddedWeekKeysMap,
      };
    });

    isFetchingNextMonthRef.current = false;
  }, []);

  const virtualizer = useVirtualizer({
    count: calendarWeeks.weeks.length + EXTRA_START_ROWS_COUNT + EXTRA_END_ROWS_COUNT,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) => {
      if (index === 0) {
        return ROW_HEIGHT + WEEK_DAYS_ROW_HEIGHT;
      }

      return ROW_HEIGHT;
    },
    initialOffset: () => {
      const index = calendarWeeks.weeks.findIndex((week) => week.weekKey === TimeHelper.toWeekOfYearStringFormat(initialDate, timezone));
      // index + 1 to display current week exactly on the top
      return (index + 1) * ROW_HEIGHT;
    },
    getItemKey: (index) => {
      if (index === 0) {
        return LoaderElementKeysEnum.PREV_LOADER;
      }

      if (index === calendarWeeks.weeks.length + EXTRA_START_ROWS_COUNT) {
        return LoaderElementKeysEnum.NEXT_LOADER;
      }

      // skip loader rows
      return calendarWeeks.weeks[index - EXTRA_START_ROWS_COUNT].weekKey;
    },
    overscan: 0,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // detect scrolling up to restore scroll position when new rows are added
  // it runs on each render to make scrolling smooth
  const prevLoadedWeeks = useRef<IWeek[] | null>(null);
  if (prevLoadedWeeks.current && prevLoadedWeeks.current.length > 0) {
    const firstPrevLoadedWeek = prevLoadedWeeks.current[0];
    const firstLoadedWeek = calendarWeeks.weeks[0];

    if (firstLoadedWeek && firstPrevLoadedWeek.weekKey !== firstLoadedWeek.weekKey) {
      const offset = (virtualizer.scrollOffset || 0) + (WEEKS_PER_PAGE * ROW_HEIGHT);

      virtualizer.scrollOffset = offset;
      virtualizer.calculateRange();
      virtualizer.scrollToOffset(offset, { align: 'start' });
    }
  }
  prevLoadedWeeks.current = calendarWeeks.weeks;

  useEffect(() => {
    if (virtualItems.length <= (EXTRA_START_ROWS_COUNT + EXTRA_END_ROWS_COUNT)) {
      return;
    }

    const lastItem = virtualItems[virtualItems.length - EXTRA_START_ROWS_COUNT];
    const firstItem = virtualItems[0];

    if (lastItem.index === calendarWeeks.weeks.length + 1 && !isFetchingNextMonthRef.current) {
      handleLoadNextMonth(calendarWeeks.weeks, timezone);
    } else if (firstItem.index < EXTRA_START_ROWS_COUNT && !isFetchingPrevMonthRef.current) {
      handleLoadPrevMonth(calendarWeeks.weeks, timezone);
    }
  }, [virtualItems, calendarWeeks]);

  return (
    <LoadingWrapper loading={!isInitialized}>
      <CalendarGrid
        containerRef={containerRef}
        virtualizer={virtualizer}
        virtualItems={virtualItems}
        calendarWeeks={calendarWeeks}
        timezone={timezone}
        DayContentSlot={DayContentSlot}
      />
    </LoadingWrapper>
  );
});
